import { useState } from "react";
import {
  CheckCircle,
  Circle,
  Trash,
  ArrowBendUpLeft,
  Plus,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { cn } from "../../../lib/cn";

type Filter = "all" | "open" | "resolved";

const AVATAR_PALETTE = [
  "#3B82F6", "#8B5CF6", "#EC4899", "#06B6D4",
  "#22C55E", "#F59E0B", "#EF4444", "#F97316",
];

function getUserColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function getInitials(id: string): string {
  return id.slice(0, 2).toUpperCase();
}

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
];

interface MockComment {
  id: string;
  userId: string;
  lineStart: number;
  lineEnd: number;
  content: string;
  time: string;
  resolved: boolean;
  replies: MockReply[];
}

interface MockReply {
  id: string;
  userId: string;
  content: string;
  time: string;
}

const MOCK_COMMENTS: MockComment[] = [
  {
    id: "c1",
    userId: "Alex Chen",
    lineStart: 24,
    lineEnd: 24,
    content: "Should we add more context to the introduction about prior work?",
    time: "2h",
    resolved: false,
    replies: [
      {
        id: "r1",
        userId: "You",
        content: "Good idea, I'll add a paragraph referencing the related work section.",
        time: "1h",
      },
    ],
  },
  {
    id: "c2",
    userId: "Sam Lee",
    lineStart: 58,
    lineEnd: 62,
    content: "The methodology section looks great. Consider adding a figure for the pipeline.",
    time: "5h",
    resolved: false,
    replies: [],
  },
];

export function DemoCommentsPanel() {
  const [filter, setFilter] = useState<Filter>("all");

  const counts = {
    all: MOCK_COMMENTS.length,
    open: MOCK_COMMENTS.filter((c) => !c.resolved).length,
    resolved: MOCK_COMMENTS.filter((c) => c.resolved).length,
  };

  const filtered =
    filter === "open"
      ? MOCK_COMMENTS.filter((c) => !c.resolved)
      : filter === "resolved"
        ? MOCK_COMMENTS.filter((c) => c.resolved)
        : MOCK_COMMENTS;

  return (
    <div className="flex h-full flex-col">
      {/* Header — matches real CommentThread */}
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Comments
        </span>
        <div className="flex items-center gap-1">
          <span className="text-[10px] tabular-nums text-[var(--text-muted)]">
            {counts.open}
          </span>
          <div
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-muted)] opacity-40",
            )}
          >
            <Plus size={13} />
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-0.5 px-2.5 pb-1.5">
        {FILTERS.map((f) => {
          const count = counts[f.id];
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                "relative px-2 py-0.5 text-[10px] font-medium rounded-[var(--radius-sm)]",
                "transition-colors duration-75",
                filter === f.id
                  ? "text-[var(--text-primary)] bg-[var(--bg-surface-hover)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
              )}
            >
              {f.label}
              {count > 0 && (
                <span className="ml-1 tabular-nums opacity-60">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 px-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]">
              <ChatCircleDots size={20} weight="duotone" className="text-[var(--text-muted)]" />
            </div>
            <p className="text-[11px] text-[var(--text-muted)] text-center leading-relaxed">
              {filter === "resolved" ? "No resolved comments." : "No open comments."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {filtered.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentItem({ comment }: { comment: MockComment }) {
  const color = getUserColor(comment.userId);
  const initials = getInitials(comment.userId);

  return (
    <article
      className={cn(
        "group relative rounded-[var(--radius-sm)] px-2 py-1.5 cursor-pointer",
        "transition-colors duration-75",
        "hover:bg-[var(--bg-surface-hover)]",
        comment.resolved && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2">
        {/* Initials avatar — matches real CommentThread */}
        <div
          className="flex shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white select-none"
          style={{ width: 18, height: 18, backgroundColor: color }}
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-medium text-[var(--text-primary)] truncate">
              {comment.userId}
            </span>
            {/* Line badge — matches real CommentItem */}
            <span
              className={cn(
                "text-[9px] font-mono tabular-nums px-1 py-px rounded-[var(--radius-sm)]",
                "text-[var(--color-cool-azure)] bg-[var(--color-cool-azure)]/8",
              )}
            >
              L{comment.lineStart}
              {comment.lineStart !== comment.lineEnd ? `–${comment.lineEnd}` : ""}
            </span>
            <span className="text-[10px] text-[var(--text-muted)]">{comment.time}</span>
          </div>

          <p className="mt-0.5 text-[12px] text-[var(--text-secondary)] leading-relaxed">
            {comment.content}
          </p>

          {comment.replies.length > 0 && (
            <span className="mt-0.5 text-[10px] text-[var(--text-muted)]">
              {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
            </span>
          )}
        </div>

        {/* Hover actions — decorative */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-75">
          <div
            className={cn(
              "p-0.5 rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--color-signal-green)]",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
            )}
          >
            {comment.resolved ? (
              <CheckCircle size={13} weight="fill" />
            ) : (
              <Circle size={13} />
            )}
          </div>
          <div
            className={cn(
              "p-0.5 rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--color-soft-red)]",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
            )}
          >
            <Trash size={13} />
          </div>
        </div>
      </div>

      {/* Reply button */}
      {!comment.resolved && (
        <div className="mt-1 ml-[26px]">
          <button
            className={cn(
              "flex items-center gap-1 text-[10px] text-[var(--text-muted)]",
              "hover:text-[var(--text-secondary)] transition-colors duration-75",
              "opacity-0 group-hover:opacity-100",
            )}
          >
            <ArrowBendUpLeft size={11} />
            Reply
          </button>
        </div>
      )}

      {/* Nested replies */}
      {comment.replies.length > 0 && (
        <div className="mt-1.5 space-y-0.5 ml-[26px]">
          {comment.replies.map((reply) => {
            const replyColor = getUserColor(reply.userId);
            const replyInitials = getInitials(reply.userId);
            return (
              <div
                key={reply.id}
                className={cn(
                  "relative rounded-[var(--radius-sm)] px-2 py-1.5 cursor-pointer",
                  "transition-colors duration-75",
                  "ml-5 pl-2.5",
                  "hover:bg-[var(--bg-surface-hover)]",
                )}
              >
                <div className="absolute left-0 top-0 bottom-0 w-px bg-[var(--border-subtle)]" />
                <div className="flex items-start gap-2">
                  <div
                    className="flex shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white select-none"
                    style={{ width: 18, height: 18, backgroundColor: replyColor }}
                  >
                    {replyInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium text-[var(--text-primary)]">
                        {reply.userId}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)]">{reply.time}</span>
                    </div>
                    <p className="mt-0.5 text-[12px] text-[var(--text-secondary)] leading-relaxed">
                      {reply.content}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
}
